import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";

// TRIMY Shield Logo Component
const TrimyLogo = () => (
    <svg width="64" height="64" viewBox="0 0 48 48" fill="none">
        <defs>
            <linearGradient id="loginShieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#00D4FF" />
                <stop offset="100%" stopColor="#12006d" />
            </linearGradient>
        </defs>
        <path 
            d="M24 4L6 10v12c0 11 8 18 18 22 10-4 18-11 18-22V10L24 4z" 
            fill="url(#loginShieldGrad)" 
        />
        <path 
            d="M24 12v6M24 22v2M18 16h12M16 22h4M28 22h4" 
            stroke="white" 
            strokeWidth="2" 
            strokeLinecap="round" 
        />
        <circle cx="24" cy="28" r="2" fill="white" />
        <path d="M20 32h8" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

export default function LoginPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await login(email, password);
            toast.success("Welcome back!");
            navigate("/dashboard");
        } catch (error) {
            toast.error(error.response?.data?.detail || "Invalid credentials");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="flex flex-col items-center mb-8">
                    <div className="mb-4">
                        <TrimyLogo />
                    </div>
                    <h1 className="font-heading text-3xl font-bold text-[#12006d] tracking-wide">TRIMY</h1>
                    <p className="text-gray-500 text-sm mt-1">Business Management Made Simple</p>
                </div>

                <Card className="border-0 shadow-xl shadow-[#12006d]/5">
                    <CardHeader className="text-center pb-2">
                        <CardTitle className="font-heading text-xl text-[#12006d]">Welcome Back</CardTitle>
                        <CardDescription>Sign in to your account</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-gray-700">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="your@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="login-email-input"
                                    className="h-12 rounded-xl border-gray-200 focus:border-[#00D4FF] focus:ring-[#00D4FF]/20"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-gray-700">Password</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        data-testid="login-password-input"
                                        className="h-12 pr-12 rounded-xl border-gray-200 focus:border-[#00D4FF] focus:ring-[#00D4FF]/20"
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 text-gray-400 hover:text-gray-600"
                                        onClick={() => setShowPassword(!showPassword)}
                                        data-testid="toggle-password-visibility"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="w-5 h-5" />
                                        ) : (
                                            <Eye className="w-5 h-5" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                            
                            <div className="flex justify-end">
                                <Link 
                                    to="/forgot-password" 
                                    className="text-sm text-[#00D4FF] hover:text-[#12006d] transition-colors"
                                    data-testid="forgot-password-link"
                                >
                                    Forgot password?
                                </Link>
                            </div>
                            
                            <Button 
                                type="submit" 
                                className="w-full h-12 rounded-xl font-semibold text-white"
                                style={{ 
                                    background: 'linear-gradient(135deg, #00D4FF 0%, #12006d 100%)',
                                    boxShadow: '0 4px 12px rgba(0, 212, 255, 0.3)'
                                }}
                                disabled={loading}
                                data-testid="login-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Signing in...
                                    </>
                                ) : (
                                    "Sign In"
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 text-center text-sm">
                            <span className="text-gray-500">Don&apos;t have an account? </span>
                            <Link 
                                to="/register" 
                                className="text-[#00D4FF] font-medium hover:text-[#12006d] transition-colors"
                                data-testid="register-link"
                            >
                                Sign up
                            </Link>
                        </div>
                    </CardContent>
                </Card>
                
                {/* Footer */}
                <p className="text-center text-xs text-gray-400 mt-6">
                    © {new Date().getFullYear()} TRIMY. All rights reserved.
                </p>
            </div>
        </div>
    );
}
